import { useCallback } from "react";
import HttpMethods from "system/types/enums/HttpMethods";
import { IRetryOptions } from "system/types/interfaces";
import IAuth from "system/types/interfaces/IAuth";

const useHttpRequest = (apiUrl: string, auth: IAuth) => {
  const retry = async <T>(fn: () => Promise<T>, options: IRetryOptions = {}): Promise<T> => {
    const { retries = 0, baseDelay = 1000, maxDelay = 10000, signal } = options;

    let lastError: any;

    for (let attempt = 0; attempt <= retries; attempt++) {
      if (signal?.aborted) {
        throw new Error("Request aborted");
      }

      try {
        return await fn();
      } catch (error) {
        lastError = error;
        if (attempt >= retries) {
          throw error;
        }

        if (lastError.isTransient) {
          const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
          const jitter = Math.random() * 100;
          await new Promise((resolve) => setTimeout(resolve, delay + jitter));
        } else {
          throw lastError;
        }
      }
    }

    throw lastError;
  };

  return useCallback(
    async (method: HttpMethods, action: string, body?: any, blob?: boolean, retryOptions?: IRetryOptions, responseType?: 'json' | 'blob' | 'text', customHeaders?: Record<string, string>): Promise<any> => {
      auth.extendSession(action);
      const token = await auth.getToken();
      if (!token) {
        console.warn(`Api ${method.toString()} request was not sent, not authenticated.`);
        return null;
      }

      const fetchRequest = async () => {
        const requestUrl = /^https?:\/\//i.test(action) ? action : `${apiUrl}${action}`;
        const isFormData = body instanceof FormData;
        const headers: Record<string, string> = {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          ...(customHeaders || {}),
        };

        if (!isFormData) {
          headers["Content-Type"] = "application/json";
        }

        const response = await fetch(requestUrl, {
          method: HttpMethods[method].toUpperCase(),
          headers,
          body: isFormData ? body : JSON.stringify(body ?? undefined),
          signal: retryOptions?.signal,
        });

        if (response.status === 401) {
          auth.logout();
          return null;
        }

        // A 204 (e.g. a successful delete) has no body to parse - and parsing an empty
        // body as JSON always throws - so treat it as success up front instead of falling
        // into the catch below, which would otherwise mistake this success for a failure.
        if (response.status === 204) {
          return blob || responseType === 'blob' ? null : {};
        }

        let responseBody;
        try {
          if (blob || responseType === 'blob') {
            const disposition = response.headers.get('Content-Disposition') ?? '';
            const match = disposition.match(/filename="?([^";\n]+)"?/);
            const filename = match?.[1]?.trim() ?? null;
            const blobData = await response.clone().blob();
            responseBody = filename ? { data: blobData, filename } : blobData;
          }
          else if (responseType === 'text') {
            const text = await response.clone().text();
            responseBody = text;
          } else {
            // Default: standard JSON parsing
            responseBody = await response.clone().json();
          }
        } catch {
          const rawText = await response.text();
          console.log("Server did not return expected response format: " + rawText);
          // A body that fails to parse (an HTML gateway error page on a 413/502/504, plain
          // text on a 500, etc.) must never be swallowed into a null that callers read as
          // success - surface it as a failure below instead, same as any other error body.
          responseBody = { failMessage: `The server returned an unexpected response (status ${response.status}).` };
        }

        if (responseBody !== null && typeof responseBody === "object" && !Array.isArray(responseBody) && !(responseBody instanceof Blob)) {
          responseBody.status = response.status;
        }

        if (!response.ok || responseBody?.failMessage) {
          const error: any = new Error(`HTTP error! Status: ${response.status}, Message: ${responseBody?.failMessage || "Unknown error"}`);
          error.status = response.status;
          error.isTransient = retryOptions !== undefined;
          error.responseBody = responseBody;
          throw error;
        }

        return responseBody;
      };

      try {
        return await retry(fetchRequest, retryOptions);
      } catch (error: any) {
        if (error.responseBody) {
          return error.responseBody;
        }
        throw error;
      }
    },
    [auth, apiUrl]
  );
};

export default useHttpRequest;
